import { useParams } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Page, Header, Content, Progress, ErrorPage } from '@backstage/core-components';

type PageMeta = {
  title: string;
};

type PageContent = {
  html: string;
};

async function loadPage(
  baseUrl: string,
  fetchImpl: typeof fetch,
  pageId: string,
): Promise<{ title: string; html: string } | undefined> {
  const metaRes = await fetchImpl(`${baseUrl}/pages/${pageId}`);
  if (metaRes.status === 404) {
    return undefined;
  }
  if (!metaRes.ok) {
    throw new Error(`Failed to load page metadata (${metaRes.status})`);
  }
  const meta: PageMeta = await metaRes.json();

  const contentRes = await fetchImpl(`${baseUrl}/pages/${pageId}/content`);
  if (contentRes.status === 404) {
    return undefined;
  }
  if (contentRes.status === 400) {
    // A config-time problem with the entity's own annotations (bad/missing
    // path, unrecognized managed-by-location), not an access decision -- the
    // backend deliberately makes this distinguishable from the generic 404
    // above so it's debuggable, so don't collapse it back into "not found".
    const body = await contentRes.json().catch(() => undefined);
    throw new Error(body?.error ?? 'Page is misconfigured (400 Bad Request)');
  }
  if (!contentRes.ok) {
    throw new Error(`Failed to load page content (${contentRes.status})`);
  }
  const content: PageContent = await contentRes.json();

  return { title: meta.title, html: content.html };
}

/**
 * Renders a static-external-page's content in a sandboxed iframe. The HTML
 * comes verbatim from a GitHub repo (see static-external-pages-backend), so
 * it's rendered via `srcDoc` in a sandboxed frame -- scripts can run (reports
 * commonly need charting JS) but the frame gets no access to this app's
 * origin, cookies, or parent window.
 */
export function StaticExternalPageContent() {
  const { pageId } = useParams<{ pageId: string }>();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: fetchImpl } = useApi(fetchApiRef);

  const { value, loading, error } = useAsync(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('static-external-pages');
    return loadPage(baseUrl, fetchImpl, pageId as string);
  }, [discoveryApi, fetchImpl, pageId]);

  if (loading) {
    return (
      <Page themeId="tool">
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <ErrorPage
        status="500"
        statusMessage="Failed to load page"
        additionalInfo={error.message}
      />
    );
  }

  if (!value) {
    return <ErrorPage status="404" statusMessage="Page not found" />;
  }

  return (
    <Page themeId="tool">
      <Header title={value.title} />
      <Content noPadding stretch>
        <iframe
          title={value.title}
          srcDoc={value.html}
          sandbox="allow-scripts allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox"
          style={{ width: '100%', height: '100%', minHeight: '80vh', border: 'none' }}
        />
      </Content>
    </Page>
  );
}
